import { CommonModule } from '@angular/common';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, signal, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { SideNavItemComponent } from '../sidenav/sidenav.item.component';
import { SideNavItemBaseComponent } from '../sidenav/sidenav.item.base.component';
import { EnvironmentConfiguration, HelpConfiguration } from '@jms/model';
import { HelpMenuItem } from './models';
import { HelpService, IntercomDragWidgetService, UserflowDragWidgetService } from './services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, tap } from 'rxjs';
import Enumerable from 'linq';

import userflow from 'userflow.js'
import { Intercom } from "@intercom/messenger-js-sdk";

import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';

@Component({
    selector: 'help-nav',
    templateUrl: './help.nav.component.html',
    styleUrls: ['./help.nav.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, OverlayModule, CdkOverlayOrigin, CdkConnectedOverlay]
})
export class HelpNavComponent extends SideNavItemBaseComponent {

    protected $canShowLauncher = signal(false);
    @Input() public navExpanded = false;
    protected helpMenu: HelpMenuItem;

    protected hasBadge = false;
    protected trial = false;
    protected maxUnreadCount = 9;
    protected positions = [{
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetY: -60
    } as ConnectedPosition];

    // private messengerContainerElem: any;
    // private frameElem: any;
    // private indicatorElem: any;

    // private offset = [0, 0];
    // private isDown = false;

    // private triedCount = 0;

    // private lastMousePosition: [number, number] = [0, 0];
    // private documentDimension: [number, number] = [0, 0];
    // private edge = 20;
    // private framePointerEvents: any;

    // private documentMouseMoveHandlerContext = this.documentMouseMoveHandler.bind(this);
    // private documentMouseUpHandlerContext = this.documentMouseUpHandler.bind(this);
    // private windowResizeHandlerContext = this.windowResizeHandler.bind(this);
    // private messengerMouseDownHandlerContext = this.messengerMouseDownHandler.bind(this);
    // private dialogOffsetWith = 0;
    // private isCut = false;
    // private widthFromEdge = 0;

    private environment: EnvironmentConfiguration;
    private helpData: Record<"tickets" | "help-center" | 'full-changelog' | 'release-note', string>;

    private intercomDragWidgetService: IntercomDragWidgetService;
    private userflowDragWidgetService: UserflowDragWidgetService;

    constructor(
        private helpService: HelpService,
        public overlay: Overlay,
        public cdr: ChangeDetectorRef,
        public router: Router,
        private userService: CurrentUserService
    ) {
        super(cdr);
        this.createMenu();
        this.environment = ConfigService.environment;
        this.intercomDragWidgetService = new IntercomDragWidgetService();
        this.userflowDragWidgetService = new UserflowDragWidgetService();

        this.helpService.getConfig()
            .pipe(
                takeUntilDestroyed(),
                tap((helpConfig) => {
                    if (!helpConfig) {
                        return;
                    }

                    this.setupHelp(helpConfig);
                }),
                catchError((error) => {
                    console.error(error);
                    return of();
                })
            )
            .subscribe()
    }

    ngOnInit() {

    }

    private setupHelp(helpConfig: HelpConfiguration) {
        this.trial = this.environment.trial;
        const canShowLauncher = (!this.trial && !!this.userService.user && !!this.environment.help.intercom);
        this.$canShowLauncher.set(canShowLauncher);

        this.setUpIntercom(canShowLauncher, helpConfig);
        this.setupUserFlow(canShowLauncher, helpConfig);
    }

    onAttached() {
        SideNavItemComponent.attached$.next(this);
    }

    onForceClose() {
        SideNavItemComponent.attached$.next(null);
    }

    private createMenu() {
        const children: HelpMenuItem[] = [
            {
                id: "help-title",
                title: "Help",
                children: [
                    {
                        "id": "chat",
                        "title": "Chat",
                        "icon": "fa-solid fa-message-question",
                        "badgeNumber": 0,
                        "showNumber": true,
                        "isLink": false,
                        "action": () => {
                            this.open = false;
                            window.Intercom('show');
                        }
                    },
                    {
                        "id": "help-center",
                        "title": "Help Centre",
                        "icon": "fa-solid fa-book-open",
                        "isLink": true,
                        action: () => {
                            window.open(this.helpData["help-center"], '_blank');
                        }
                    },
                    {

                        "id": "tickets",
                        "title": "Tickets",
                        "icon": "fa-solid fa-ticket-simple",
                        "badgeNumber": 0,
                        "showNumber": true,
                        isLink: true,
                        action: () => {
                            window.open(this.helpData["tickets"], '_blank');
                        }
                    },
                    // {
                    //     "id": "resources-center",
                    //     "title": "Resources Centre",
                    //     "icon": "fa-solid fa-rectangle-list",
                    //     "badgeNumber": 0,
                    //     "showNumber": true,
                    //     action: () => {
                    //         userflow.toggleResourceCenter();
                    //         this.toggleUserflowState();
                    //     }
                    // },
                ]
            },
            {
                id: "whats-new-title",
                title: "What's new",
                hasLine: true,
                children: [
                    {

                        "id": "whats-new-change-log",
                        "title": "Latest Release Notes",
                        "icon": "fa-solid fa-circle-dot",
                        "isLink": true,
                        action: () => {
                            window.open(this.helpData["release-note"], '_blank');
                        }
                    },
                    {

                        "id": "whats-new-change-log",
                        "title": "Full Change Log",
                        "icon": "fa-solid fa-circle",
                        "isLink": true,
                        action: () => {
                            window.open(this.helpData["full-changelog"], '_blank');
                        }
                    },
                ]
            },
        ]

        this.helpMenu = {
            "id": "help-main-menu",
            "title": "Help",
            "icon": "fa-solid fa-circle-question",
            "children": children
        }

        this.hasBadge = Enumerable.from(children).selectMany(e => e.children).any(x => x.badgeNumber > 0);
    }

    private toggleUserflowState() {
        const state = userflow.getResourceCenterState();

        if (state.isOpen) {
            this.userflowDragWidgetService.beginMoving();
        }
        else {
            this.userflowDragWidgetService.unregisterHandler();
        }
    }

    private updateBadgeNumber(menuId: string, count: number) {
        const menu = Enumerable.from(this.helpMenu.children).selectMany(e => e.children).firstOrDefault(x => x.id === menuId);
        if (menu) {
            menu.badgeNumber = count;
        }

        this.hasBadge = Enumerable.from(this.helpMenu.children).selectMany(e => e.children).any(x => x.badgeNumber > 0);
    }

    private setUpIntercom(canShowLauncher: boolean, helpConfig: HelpConfiguration) {
        if (canShowLauncher && helpConfig) {

            const intercom = this.environment.help.intercom;
            this.helpData = {
                "help-center": intercom.helpBaseUrl,
                "tickets": `${intercom.helpBaseUrl}/en/tickets-portal`,

                "full-changelog": `${intercom.helpBaseUrl}/full-change-log`,
                "release-note": `${intercom.helpBaseUrl}/latest-release-notes`
            }

            const user = this.userService.user;
            const info = {
                app_id: intercom.appId,
                name: user.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
                email: user.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
                user_id: user._id,
                created_at: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : Math.floor(new Date().getTime() / 1000),// IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
                user_hash: helpConfig.userHash.intercom,
                hide_default_launcher: true,
                custom_launcher_selector: '#messenger-launcher',
                vertical_padding: 20,
                company: {
                    company_id: helpConfig.company?.system?.id,
                    name: helpConfig.company?.system?.name,
                },
                //horizontal_padding: this.navExpanded ? 250 : 95,
                // companies: helpConfig.company?.system?.id ? [
                //     {
                //         company_id: helpConfig.company?.system?.id,
                //         name: helpConfig.company?.system?.name
                //     }
                // ] as any : undefined
            };

            Intercom(info);

            window.Intercom("onUnreadCountChange", (count) => {
                this.updateBadgeNumber("chat", count ?? 0);
                //this.updateBadgeNumber("chat", 1);
                this.cdr.markForCheck();
            });

            window.Intercom("onShow", () => {
                window.Intercom('update', { "horizontal_padding": this.navExpanded ? 245 : 95 });
                this.active = true;
                this.cdr.markForCheck();
                this.intercomDragWidgetService.beginMoving();
            });

            window.Intercom("onHide", () => {
                this.active = false;
                this.intercomDragWidgetService.unregisterHandler();
                this.cdr.markForCheck();
            });

            const company = helpConfig.company?.intercom;
            if (company) {
                if (company.id) {
                    this.helpData["tickets"] = `${this.environment.help.intercom.helpBaseUrl}/en/tickets-portal/companies/${company.id}?owner=self`;
                }
            }
        }
    }

    private async setupUserFlow(canShowLauncher: boolean, helpConfig: HelpConfiguration) {
        if (!canShowLauncher || !helpConfig) {
            return;
        }

        const user = this.userService.user;
        const userflowConfig = this.environment.help.userflow;

        userflow.init(userflowConfig.token)
        userflow.setResourceCenterLauncherHidden(true);

        userflow.identify(user._id, {
            name: user.name,
            email: user.email,
            signed_up_at: new Date().toISOString(),
        }, {
            signature: helpConfig.userHash.userflow
        });


        //Groups are not part of our billing plan anymore, so we do not use them

        // if (helpConfig.company?.system?.id) {
        //     userflow.group(helpConfig.company?.system?.id,
        //         null,
        //         {
        //             signature: helpConfig.companyHash.userflow
        //         }
        //     );;
        // }

        //Rubbish userflow event - need to identify when we should do this
        // userflow.track('subscription_activated')

        userflow.on('resourceCenterChanged', (onChange) => {
            this.toggleUserflowState();
        });
    }
}
